var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/function-component-definition */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import { useQuery } from '../../hooks';
import { loadMatches } from '../../api';
import { EEventPlayerStatus, EPlayerKanbanStatus, } from '../../types';
import { PlayersKanban } from '../../components/organisms/PlayersKanban';
import { PlayerSearchBar } from '../../components/molecules/PlayerSearchBar';
import { Breadcrumbs } from '../../components/atoms/Breadcrumbs';
import { colors } from '../../styles/colors';
import { useErrorHandler } from '../../hooks/useErrorHandler';
function buildKanbanPlayers(eventPlayers) {
    return eventPlayers
        .filter((ep) => ep.status === EEventPlayerStatus.ACTIVE)
        .map((ep) => ({
        id: ep.id,
        player: ep.player,
        computedStatus: EPlayerKanbanStatus.NEW,
        overriddenStatus: null,
    }));
}
function matchesSearch(kanbanPlayer, query) {
    var _a, _b;
    if (!query.trim())
        return true;
    const q = query.toLowerCase().trim();
    const { player } = kanbanPlayer;
    return (player.realName.toLowerCase().includes(q) ||
        player.gameName.toLowerCase().includes(q) ||
        ((_b = (_a = player.telegram) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(q)) !== null && _b !== void 0 ? _b : false));
}
export const EventPlayers = () => {
    const nav = useNavigate();
    const { param: eventId } = useQuery('eventId');
    const { errorHandler } = useErrorHandler();
    const [event, setEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // Local override map: kanbanPlayerId -> EPlayerKanbanStatus
    const [statusOverrides, setStatusOverrides] = useState({});
    const loadEventData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!eventId)
            return;
        try {
            setIsLoading(true);
            const result = yield loadMatches(eventId);
            setEvent(result);
        }
        catch (e) {
            errorHandler(e);
        }
        finally {
            setIsLoading(false);
        }
    }), [eventId]);
    useEffect(() => {
        loadEventData();
    }, [loadEventData]);
    const allKanbanPlayers = useMemo(() => {
        if (!(event === null || event === void 0 ? void 0 : event.players))
            return [];
        return buildKanbanPlayers(event.players).map((kp) => {
            var _a;
            return (Object.assign(Object.assign({}, kp), { overriddenStatus: (_a = statusOverrides[kp.id]) !== null && _a !== void 0 ? _a : null }));
        });
    }, [event === null || event === void 0 ? void 0 : event.players, statusOverrides]);
    const filteredKanbanPlayers = useMemo(() => {
        return allKanbanPlayers.filter((kp) => matchesSearch(kp, searchQuery));
    }, [allKanbanPlayers, searchQuery]);
    const handleStatusChange = useCallback((kanbanPlayerId, newStatus) => {
        setStatusOverrides((prev) => (Object.assign(Object.assign({}, prev), { [kanbanPlayerId]: newStatus })));
    }, []);
    const handlePlayerClick = useCallback((kanbanPlayer) => {
        // Future: open player detail modal
        console.log('Player clicked:', kanbanPlayer.player.realName);
    }, []);
    const onClickBack = () => {
        if (eventId) {
            nav(`/matches?eventId=${eventId}`);
        }
        else {
            nav('/');
        }
    };
    const totalActive = allKanbanPlayers.length;
    return (_jsx(Box, Object.assign({ sx: {
            minHeight: '100vh',
            backgroundColor: colors.background,
            padding: '24px',
        } }, { children: _jsxs(Box, Object.assign({ sx: { maxWidth: '1200px', margin: '0 auto' } }, { children: [_jsx(Breadcrumbs, { items: [
                        { label: 'Афиши', path: '/' },
                        { label: (event === null || event === void 0 ? void 0 : event.date) || 'Игры вечера', path: eventId ? `/matches?eventId=${eventId}` : '/' },
                        { label: 'Игроки' },
                    ] }), _jsxs(Box, Object.assign({ sx: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '24px',
                        flexWrap: 'wrap',
                        gap: '16px',
                    } }, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: '16px' } }, { children: [_jsx(IconButton, Object.assign({ onClick: onClickBack, sx: {
                                        backgroundColor: colors.surfaceVariant,
                                        '&:hover': { backgroundColor: colors.secondaryLight },
                                    } }, { children: _jsx(ArrowBackIcon, {}) })), _jsxs(Box, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: '12px' } }, { children: [_jsx(PeopleIcon, { sx: { fontSize: 28, color: colors.primary } }), _jsx(Typography, Object.assign({ variant: "h4" }, { children: "\u0418\u0433\u0440\u043E\u043A\u0438 \u0432\u0435\u0447\u0435\u0440\u0430" }))] })), (event === null || event === void 0 ? void 0 : event.date) && (_jsxs(Typography, Object.assign({ variant: "body2", sx: { color: colors.textSecondary, marginTop: '4px' } }, { children: [event.name, " \u2014 ", event.date] })))] })] })), !isLoading && (_jsxs(Box, Object.assign({ sx: {
                                backgroundColor: colors.surfaceVariant,
                                border: `1px solid ${colors.cardBorder}`,
                                borderRadius: '10px',
                                padding: '10px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            } }, { children: [_jsx(PeopleIcon, { sx: { fontSize: 18, color: colors.textSecondary } }), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: colors.textSecondary } }, { children: "\u0412\u0441\u0435\u0433\u043E \u0438\u0433\u0440\u043E\u043A\u043E\u0432:" })), _jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: 700, color: colors.textPrimary } }, { children: totalActive }))] })))] })), _jsx(Box, Object.assign({ sx: { marginBottom: '24px', maxWidth: '480px' } }, { children: _jsx(PlayerSearchBar, { value: searchQuery, onChange: setSearchQuery, placeholder: "\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u0438\u043C\u0435\u043D\u0438, \u043D\u0438\u043A\u0443, Telegram..." }) })), isLoading && (_jsx(Box, Object.assign({ sx: { textAlign: 'center', padding: '60px 0' } }, { children: _jsx(Typography, Object.assign({ variant: "body1", sx: { color: colors.textSecondary } }, { children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0438\u0433\u0440\u043E\u043A\u043E\u0432..." })) }))), !isLoading && totalActive === 0 && (_jsxs(Paper, Object.assign({ sx: {
                        padding: '60px 24px',
                        textAlign: 'center',
                    } }, { children: [_jsx(PeopleIcon, { sx: { fontSize: 64, color: colors.textHint, marginBottom: '16px' } }), _jsx(Typography, Object.assign({ variant: "h5", sx: { marginBottom: '8px' } }, { children: "\u041D\u0435\u0442 \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D\u043D\u044B\u0445 \u0438\u0433\u0440\u043E\u043A\u043E\u0432" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: colors.textSecondary } }, { children: "\u0418\u0433\u0440\u043E\u043A\u0438 \u043F\u043E\u044F\u0432\u044F\u0442\u0441\u044F \u0437\u0434\u0435\u0441\u044C \u043F\u043E\u0441\u043B\u0435 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u0438 \u043D\u0430 \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0435" }))] }))), !isLoading && totalActive > 0 && filteredKanbanPlayers.length === 0 && (_jsx(Box, Object.assign({ sx: { textAlign: 'center', padding: '40px 0' } }, { children: _jsxs(Typography, Object.assign({ variant: "body1", sx: { color: colors.textSecondary } }, { children: ["\u0418\u0433\u0440\u043E\u043A\u0438 \u043F\u043E \u0437\u0430\u043F\u0440\u043E\u0441\u0443 \u00AB", searchQuery, "\u00BB \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u044B"] })) }))), !isLoading && filteredKanbanPlayers.length > 0 && (_jsx(PlayersKanban, { players: filteredKanbanPlayers, onPlayerClick: handlePlayerClick, onStatusChange: handleStatusChange }))] })) })));
};
